/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Button, Radio, Checkbox, Icon, message } from 'antd';
import { services } from '@comall-backend-builder/core';
import { errorHandle } from '../../services';
import './category-select-modal.less';

const api = services.api;

export type CategoryRelItem = {
    categoryCode: string;
    categoryName: string;
    bigCategoryCode: string;
    bigCategoryName: string;
};

type AttrOption = {
    code: string;
    optionValue: string;
    attributeId?: string | number;
    haveRichText?: boolean;
    [key: string]: any;
};

type CategorySelectModalProps = {
    visible: boolean;
    /** 表单中已添加的品类+大类，对应项禁用 */
    existingList?: CategoryRelItem[];
    /** 归属当前规则的品类+大类（含已移除），haveRichText 时仍可再次勾选 */
    ownedList?: CategoryRelItem[];
    onCancel: () => void;
    onOk: (items: CategoryRelItem[]) => void;
};

const normalizeOption = (item: any): AttrOption => ({
    ...item,
    code: String(item.code ?? item.id ?? ''),
    optionValue: item.optionValue || item.name || '',
    attributeId: item.attributeId ?? item.attribute_id,
    haveRichText: !!item.haveRichText,
});

export const BatchDetailCategorySelectModal: React.FC<CategorySelectModalProps> = ({
    visible,
    existingList = [],
    ownedList = [],
    onCancel,
    onOk,
}) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [loading, setLoading] = useState(false);
    const [categoryKeyword, setCategoryKeyword] = useState('');
    const [bigKeyword, setBigKeyword] = useState('');
    const [categoryOptions, setCategoryOptions] = useState<AttrOption[]>([]);
    const [bigOptions, setBigOptions] = useState<AttrOption[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<AttrOption | null>(null);
    const [selectedBigCodes, setSelectedBigCodes] = useState<string[]>([]);

    const existingBigCodeSet = useMemo(() => {
        if (!selectedCategory) {
            return new Set<string>();
        }
        return new Set(
            existingList
                .filter((item) => item.categoryCode === selectedCategory.code)
                .map((item) => item.bigCategoryCode)
        );
    }, [existingList, selectedCategory]);

    const ownedBigCodeSet = useMemo(() => {
        if (!selectedCategory) {
            return new Set<string>();
        }
        return new Set(
            ownedList
                .filter((item) => item.categoryCode === selectedCategory.code)
                .map((item) => item.bigCategoryCode)
        );
    }, [ownedList, selectedCategory]);

    const isBigDisabled = (item: AttrOption) => {
        if (existingBigCodeSet.has(item.code)) {
            return true;
        }
        if (item.haveRichText && !ownedBigCodeSet.has(item.code)) {
            return true;
        }
        return false;
    };

    const loadCategories = () => {
        setLoading(true);
        api.get({ attr_code: 'ZHPPL' }, { apiPath: '/attributes/optionsList' })
            .then((res: any) => {
                const list = Array.isArray(res) ? res : res?.result || res?.data || [];
                setCategoryOptions(list.map(normalizeOption));
            })
            .catch(errorHandle)
            .finally(() => {
                setLoading(false);
            });
    };

    const loadBigCategories = (category: AttrOption) => {
        setLoading(true);
        // attribute_id 传品类 option 的 code（如 11），不是 attribute 表主键
        api.get(
            {
                attr_code: 'ZHPDL',
                attribute_id: category.code,
            },
            { apiPath: '/attributes/optionsList' }
        )
            .then((res: any) => {
                const list = Array.isArray(res) ? res : res?.result || res?.data || [];
                setBigOptions(list.map(normalizeOption));
            })
            .catch(errorHandle)
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (!visible) {
            return;
        }
        setStep(1);
        setCategoryKeyword('');
        setBigKeyword('');
        setSelectedCategory(null);
        setSelectedBigCodes([]);
        setBigOptions([]);
        loadCategories();
    }, [visible]);

    const filteredCategories = useMemo(() => {
        const kw = categoryKeyword.trim();
        if (!kw) {
            return categoryOptions;
        }
        return categoryOptions.filter(
            (item) => item.code.includes(kw) || item.optionValue.includes(kw)
        );
    }, [categoryOptions, categoryKeyword]);

    const filteredBigOptions = useMemo(() => {
        const kw = bigKeyword.trim();
        if (!kw) {
            return bigOptions;
        }
        return bigOptions.filter((item) => item.code.includes(kw) || item.optionValue.includes(kw));
    }, [bigOptions, bigKeyword]);

    const selectedBigItems = useMemo(
        () => bigOptions.filter((item) => selectedBigCodes.includes(item.code)),
        [bigOptions, selectedBigCodes]
    );

    const handleNext = () => {
        if (!selectedCategory) {
            message.warning('请选择商品品类');
            return;
        }
        setBigKeyword('');
        setSelectedBigCodes([]);
        setStep(2);
        loadBigCategories(selectedCategory);
    };

    const handlePrev = () => {
        setStep(1);
        setSelectedBigCodes([]);
        setBigOptions([]);
    };

    const handleSelectAll = () => {
        const selectableCodes = filteredBigOptions
            .filter((item) => !isBigDisabled(item))
            .map((item) => item.code);
        setSelectedBigCodes((prev) => Array.from(new Set([...prev, ...selectableCodes])));
    };

    const handleInvertSelect = () => {
        const selectableCodes = filteredBigOptions
            .filter((item) => !isBigDisabled(item))
            .map((item) => item.code);
        setSelectedBigCodes((prev) => {
            const keep = prev.filter((code) => !selectableCodes.includes(code));
            const add = selectableCodes.filter((code) => !prev.includes(code));
            return [...keep, ...add];
        });
    };

    const handleConfirm = () => {
        if (!selectedCategory) {
            message.warning('请选择商品品类');
            return;
        }
        if (!selectedBigCodes.length) {
            message.warning('请至少选择一个商品大类');
            return;
        }
        const items: CategoryRelItem[] = selectedBigItems.map((big) => ({
            categoryCode: selectedCategory.code,
            categoryName: selectedCategory.optionValue,
            bigCategoryCode: big.code,
            bigCategoryName: big.optionValue,
        }));
        onOk(items);
    };

    const renderSelectedPanel = (
        count: number,
        onClear: () => void,
        list: Array<{ key: string; label: string }>
    ) => (
        <div className="batch-detail-category-select__right">
            <div className="batch-detail-category-select__right-header">
                <span>已选 {count}</span>
                <a onClick={onClear}>清空</a>
            </div>
            <div className="batch-detail-category-select__selected-list">
                {list.map((item) => (
                    <div key={item.key} className="batch-detail-category-select__selected-item">
                        {item.label}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <Modal
            title={step === 1 ? '选择商品品类' : '选择商品大类'}
            visible={visible}
            onCancel={onCancel}
            width={720}
            destroyOnClose
            className="batch-detail-category-select"
            footer={
                step === 1
                    ? [
                          <Button key="cancel" onClick={onCancel}>
                              取消
                          </Button>,
                          <Button key="next" type="primary" onClick={handleNext}>
                              下一步
                          </Button>,
                      ]
                    : [
                          <Button key="cancel" onClick={onCancel}>
                              取消
                          </Button>,
                          <Button key="prev" onClick={handlePrev}>
                              上一步
                          </Button>,
                          <Button
                              key="ok"
                              type="primary"
                              disabled={!selectedBigCodes.length}
                              onClick={handleConfirm}
                          >
                              确定
                          </Button>,
                      ]
            }
        >
            <div className={`batch-detail-category-select__body ${loading ? 'is-loading' : ''}`}>
                {step === 1 ? (
                    <>
                        <div className="batch-detail-category-select__left">
                            <Input
                                allowClear
                                prefix={<Icon type="search" />}
                                placeholder="输入品类序号或名称"
                                value={categoryKeyword}
                                onChange={(e) => setCategoryKeyword(e.target.value)}
                            />
                            <div className="batch-detail-category-select__group-title">
                                商品品类 (A005)
                            </div>
                            <div className="batch-detail-category-select__option-list">
                                <Radio.Group
                                    value={selectedCategory?.code}
                                    onChange={(e) => {
                                        const found = categoryOptions.find(
                                            (item) => item.code === e.target.value
                                        );
                                        setSelectedCategory(found || null);
                                    }}
                                >
                                    {filteredCategories.map((item) => (
                                        <div
                                            key={item.code}
                                            className="batch-detail-category-select__option-item"
                                        >
                                            <Radio value={item.code}>
                                                {item.code} {item.optionValue}
                                            </Radio>
                                        </div>
                                    ))}
                                </Radio.Group>
                            </div>
                        </div>
                        {renderSelectedPanel(
                            selectedCategory ? 1 : 0,
                            () => setSelectedCategory(null),
                            selectedCategory
                                ? [
                                      {
                                          key: selectedCategory.code,
                                          label: `${selectedCategory.code} ${selectedCategory.optionValue}`,
                                      },
                                  ]
                                : []
                        )}
                    </>
                ) : (
                    <>
                        <div className="batch-detail-category-select__left">
                            <Input
                                allowClear
                                prefix={<Icon type="search" />}
                                placeholder="输入大类序号或名称"
                                value={bigKeyword}
                                onChange={(e) => setBigKeyword(e.target.value)}
                            />
                            <div className="batch-detail-category-select__group-title">
                                {selectedCategory
                                    ? `${selectedCategory.code} ${selectedCategory.optionValue}，大类范围：`
                                    : '大类范围：'}
                            </div>
                            <div className="batch-detail-category-select__option-list">
                                {filteredBigOptions.map((item) => {
                                    const disabled = isBigDisabled(item);
                                    return (
                                        <div
                                            key={item.code}
                                            className="batch-detail-category-select__option-item"
                                        >
                                            <Checkbox
                                                disabled={disabled}
                                                checked={
                                                    disabled
                                                        ? false
                                                        : selectedBigCodes.includes(item.code)
                                                }
                                                onChange={(e) => {
                                                    if (disabled) {
                                                        return;
                                                    }
                                                    if (e.target.checked) {
                                                        setSelectedBigCodes((prev) => [
                                                            ...prev,
                                                            item.code,
                                                        ]);
                                                    } else {
                                                        setSelectedBigCodes((prev) =>
                                                            prev.filter(
                                                                (code) => code !== item.code
                                                            )
                                                        );
                                                    }
                                                }}
                                            >
                                                {item.code} {item.optionValue}
                                                {disabled ? (
                                                    <span className="batch-detail-category-select__rule-tag">
                                                        已有规则
                                                    </span>
                                                ) : null}
                                            </Checkbox>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="batch-detail-category-select__left-actions">
                                <a onClick={handleSelectAll}>全选</a>
                                <a onClick={handleInvertSelect}>反选</a>
                            </div>
                        </div>
                        {renderSelectedPanel(
                            selectedBigItems.length,
                            () => setSelectedBigCodes([]),
                            selectedBigItems.map((item) => ({
                                key: item.code,
                                label: `${item.code} ${item.optionValue}`,
                            }))
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default BatchDetailCategorySelectModal;
